import { comparer, IObservableArray } from 'mobx';

import { withKeys } from 'bernie-core/dist/util/merge';

import { RoomModel } from 'stores/models/room-model';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';

import { InCartProperty } from 'models/multi-item/multi-item-types';

import { DateUtils } from 'utils/date-utils';

import { Cart, DateInput, InventoryType, PropertyNaturalKeyInput, PropertyRoomInput, ShoppingPathType } from '__generated__/api';

export function searchCriteriaToPropertyRoomInput(rooms: RoomModel[]): PropertyRoomInput[] | undefined {
  if (!rooms) {
    return;
  }

  return rooms.map(room => ({
    childAges: room.children?.map(child => child.age) ?? [],
    numberOfAdults: room.adults,
  }));
}

export function adaptToPropertyNaturalKeyInputs(
  paymentPlanStartDate: string,
  paymentPlanEndDate: string,
  rooms: IObservableArray<RoomModel>,
  inventoryType: InventoryType,
  roomTypeCode: string,
  ratePlanCode: string,
  ratePlanType: string,
  id: string,
  isAttach: boolean,
  roomIndex?: number,
  isMixMatchEnabled?: boolean,
  isPackage?: boolean
): PropertyNaturalKeyInput[] | undefined {
  if (!paymentPlanStartDate || !paymentPlanEndDate || paymentPlanStartDate.split(',').length !== ratePlanCode.split(',').length) {
    return;
  }

  return ratePlanCode
    .split(',')
    .filter(singleRatePlan => singleRatePlan)
    .map((singleRatePlan, idx) =>
      adaptToPropertyNaturalKeyInput(
        paymentPlanStartDate.split(',')[idx] ?? '',
        paymentPlanEndDate.split(',')[idx] ?? '',
        rooms,
        inventoryType,
        roomTypeCode,
        singleRatePlan,
        ratePlanType,
        id,
        isAttach,
        roomIndex,
        isMixMatchEnabled,
        isPackage
      )
    );
}

function adaptToPropertyNaturalKeyInput(
  paymentPlanStartDate: string,
  paymentPlanEndDate: string,
  rooms: IObservableArray<RoomModel>,
  inventoryType: InventoryType,
  roomTypeCode: string,
  ratePlanCode: string,
  ratePlanType: string,
  id: string,
  isAttach: boolean,
  roomIndex?: number,
  isMixMatchEnabled?: boolean,
  isPackage?: boolean
): any {
  let roomInput: any = rooms;
  if (isMixMatchEnabled && roomIndex !== null && roomIndex && roomIndex >= 0) {
    roomInput = [rooms[roomIndex]];
  }
  let shoppingPath: any = isAttach ? ShoppingPathType.ATTACH : ShoppingPathType.STANDALONE;
  if (isPackage) shoppingPath = ShoppingPathType.PACKAGE;

  return {
    checkIn: fromDateToBexApiDate(new Date(paymentPlanStartDate)),
    checkOut: fromDateToBexApiDate(new Date(paymentPlanEndDate)),
    id,
    inventoryType: adaptInventoryType(inventoryType),
    ratePlanId: ratePlanCode,
    roomTypeId: roomTypeCode,
    ratePlanType,
    rooms: searchCriteriaToPropertyRoomInput(roomInput),
    shoppingPath: shoppingPath,
  };
}

export function adaptToPropertyNaturalKeyInputForPackages(
  checkInDate: DateInput,
  checkOutDate: DateInput,
  rooms: IObservableArray<RoomModel>,
  inventoryType: InventoryType,
  roomTypeCode: string,
  ratePlanCode: string,
  ratePlanType: string,
  hotelId: string
): any {
  return {
    checkIn: checkInDate,
    checkOut: checkOutDate,
    id: hotelId,
    inventoryType: adaptInventoryType(inventoryType),
    ratePlanId: ratePlanCode,
    roomTypeId: roomTypeCode,
    ratePlanType: ratePlanType,
    rooms: searchCriteriaToPropertyRoomInput(rooms),
    shoppingPath: ShoppingPathType.PACKAGE,
  };
}

export function adaptToCartPropertySearchContextInput(searchCriteria: SearchCriteriaModel, regionId: string): any {
  return {
    checkIn: searchCriteria.startDate ? DateUtils.parseDateString(searchCriteria.startDate) : undefined,
    checkOut: searchCriteria.endDate ? DateUtils.parseDateString(searchCriteria.endDate) : undefined,
    rooms: toPropertyRoomInputs(searchCriteria),
    regionId,
  };
}

// TODO Remove this adaption after moving this to shared-ui.
// checkIn and checkOut in propertyNaturalKeys has additional field which is not required by property add cart mutation
export function adaptToCartPropertyPrimaryKeyInput(propertyNaturalKeys: PropertyNaturalKeyInput[]): PropertyNaturalKeyInput[] {
  return propertyNaturalKeys.map((propertyNaturalKey: PropertyNaturalKeyInput) => {
    const adaptedPropertyNaturalKey: PropertyNaturalKeyInput = {
      ...propertyNaturalKey,
      checkIn: {
        day: propertyNaturalKey?.checkIn?.day,
        month: propertyNaturalKey?.checkIn?.month,
        year: propertyNaturalKey?.checkIn?.year,
      },
      checkOut: {
        day: propertyNaturalKey?.checkOut?.day,
        month: propertyNaturalKey?.checkOut?.month,
        year: propertyNaturalKey?.checkOut?.year,
      },
    };

    return adaptedPropertyNaturalKey;
  });
}

function toPropertyRoomInputs(searchCriteria: SearchCriteriaModel): PropertyRoomInput[] | undefined {
  if (!searchCriteria.rooms || searchCriteria.rooms.length === 0) {
    return;
  }

  return searchCriteria.rooms.map(room => ({
    childAges: (room.children && room.children.map(child => child.age)) || [],
    numberOfAdults: room.adults,
  }));
}

const getFeesDescriptions = (fees: any = []) => fees.map(({ description }: any) => description).filter((x: any) => x);

const getFencingAttributes = (ratePlanPriceDetails: any) =>
  ratePlanPriceDetails?.offerBookButton?.inputs
    .filter((input: { name: string }) => input && input.name === 'fencingAttributes')
    .map((input: { value: any }) => input.value)[0];

const getAvailability = (allItems: any, index: number, item: any, ratePlanPriceDetails: any) => {
  const minRoomsLeft = ratePlanPriceDetails?.availability?.minRoomsLeft;
  const available = ratePlanPriceDetails?.availability?.available;
  const notMinRoomsLeft = minRoomsLeft === null || minRoomsLeft > numberOfDuplicates(allItems.slice(0, index), item);

  return available && notMinRoomsLeft;
};

const getRoomImage = (gallery: any, isMixMatchEnabled: boolean, offers: any) =>
  isMixMatchEnabled ? offers[0]?.offer?.units[0]?.photos[0]?.url : gallery?.featured?.url;

const getAdultsAndChildren = (
  firstNaturalKey: any,
  firstNaturalKeyRoom: { numberOfAdults: any; childAges: any } | undefined,
  isMixMatchEnabled: boolean
) => {
  const adults = isMixMatchEnabled && firstNaturalKeyRoom ? firstNaturalKeyRoom.numberOfAdults : numberOfAdults(firstNaturalKey.rooms);
  const children =
    isMixMatchEnabled && firstNaturalKeyRoom?.childAges ? firstNaturalKeyRoom.childAges.length : numberOfChildren(firstNaturalKey.rooms);

  return { adults, children };
};

export function cartItemToInCartProperty(isMixMatchEnabled: boolean) {
  return (item: Cart.items, index: number, allItems: Cart.items[]): InCartProperty => {
    const { id, summary, offers, gallery } = item;
    const unit = offers[0]?.offer?.units[0];
    const marketingChannelId = offers[0]?.offer?.marketingChannelId || undefined;
    const ratePlanPriceDetails = unit?.ratePlans[0]?.priceDetails[0];
    const leadPrice = ratePlanPriceDetails?.price?.lead;
    const totalPrice = ratePlanPriceDetails?.price?.total;
    const priceWithLoyaltyPoints = ratePlanPriceDetails?.priceAfterLoyaltyPointsApplied?.lead;
    const fencingAttributes = getFencingAttributes(ratePlanPriceDetails);
    const fees = getFeesDescriptions(ratePlanPriceDetails?.fees);
    const available = getAvailability(allItems, index, item, ratePlanPriceDetails);
    const offerIndex = offers[0]?.offerIndex ?? 0;
    const naturalKeys = offers[0]?.naturalKeys;
    const firstNaturalKey = naturalKeys?.[0];
    const rooms = firstNaturalKey?.rooms || [];
    const firstNaturalKeyRoom = rooms[index];
    const lastNaturalKey = naturalKeys?.[naturalKeys.length - 1];
    const propertyId = Number(firstNaturalKey?.id);
    const roomImage = getRoomImage(gallery, isMixMatchEnabled, offers);
    const { adults, children } = getAdultsAndChildren(firstNaturalKey, firstNaturalKeyRoom, isMixMatchEnabled);

    return {
      id,
      marketingChannelId,
      name: summary?.name,
      checkin: firstNaturalKey?.checkIn?.isoFormat ?? '',
      checkout: lastNaturalKey?.checkOut?.isoFormat ?? '',
      propertyId,
      image: roomImage,
      city: summary?.location?.address?.city || '',
      adults,
      children,
      rooms: firstNaturalKey?.rooms?.length ?? 0,
      ratePlan: {
        roomName: unit?.name || undefined,
        available,
        leadPrice: {
          currencySymbol: leadPrice?.currency?.symbol,
          formatted: leadPrice?.formatted,
          raw: leadPrice?.raw,
        },
        totalPrice: {
          currencySymbol: totalPrice?.currency?.symbol,
          formatted: totalPrice?.formatted,
          raw: totalPrice?.raw,
        },
        priceAfterLoyaltyPointsApplied: {
          currencySymbol: priceWithLoyaltyPoints?.currency?.symbol,
          formatted: priceWithLoyaltyPoints?.formatted,
          raw: priceWithLoyaltyPoints?.raw,
        },
        fees,
      },
      offerIndex,
      fencingAttributes,
    };
  };
}

export function fromDateToBexApiDate(date: Date): DateInput | undefined {
  if (!date) return;

  return {
    day: date.getDate(),
    month: date.getMonth() + 1,
    year: date.getFullYear(),
  };
}

function numberOfDuplicates(items: Cart.items[], item: Cart.items) {
  return items
    .filter(otherItem => {
      const compareKeys = ['id', 'roomTypeId', 'checkIn', 'checkOut'];

      return comparer.structural(
        withKeys(compareKeys).merge({}, item.offers[0]?.naturalKeys[0]),
        withKeys(compareKeys).merge({}, otherItem.offers[0]?.naturalKeys[0])
      );
    })
    .map(i => i.offers[0]?.naturalKeys[0]?.rooms.length ?? 0)
    .reduce((acc, roomsCount) => acc + roomsCount, 0);
}

export function numberOfAdults(rooms: Cart.rooms[]): number {
  if (!rooms?.length) return 0;

  return rooms.reduce((acc, room) => acc + room.numberOfAdults, 0);
}

export function numberOfChildren(rooms: Cart.rooms[]): number {
  if (!rooms?.length) return 0;

  return rooms.reduce((acc, room) => {
    const roomChildren = room?.childAges?.length || 0;

    return acc + roomChildren;
  }, 0);
}

export function adaptInventoryType(inventoryType: InventoryType): InventoryType {
  return inventoryType ? InventoryType[inventoryType] : InventoryType.MERCHANT;
}
